import React, { useEffect, useRef, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { Link } from "react-router-dom";
import Icon from "@mdi/react";
import { mdiContentSave, mdiRefresh, mdiLogout, mdiOpenInNew } from "@mdi/js";
import { auth } from "../../lib/firebase";
import { saveTheme, saveSite } from "../../lib/settings";
import { useTheme } from "../../theme/ThemeProvider";
import { normalizeTheme } from "../../theme/themeUtils";
import { defaultTheme } from "../../theme/defaultTheme";
import { defaultSite } from "../../content/site";
import Seo from "../../components/Seo";
import Login from "./Login";
import ThemeEditor from "./ThemeEditor";
import SiteEditor from "./SiteEditor";

const stripMeta = (obj) => {
  if (!obj) return obj;
  const { updatedAt, ...rest } = obj;
  return rest;
};

const Admin = () => {
  const [user, setUser] = useState(undefined);
  const { theme, site, previewTheme, setSiteOverrides } = useTheme();
  const [tab, setTab] = useState("theme");
  const [draftTheme, setDraftTheme] = useState(theme);
  const [draftSite, setDraftSite] = useState(site);
  const [dirty, setDirty] = useState(false);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const frame = useRef(null);

  useEffect(() => onAuthStateChanged(auth, (u) => setUser(u || null)), []);

  // Firestore'dan ayarlar geç gelirse taslağı güncelle (kullanıcı henüz değiştirmediyse)
  useEffect(() => {
    if (!dirty) {
      setDraftTheme(theme);
      setDraftSite(site);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, site]);

  const post = (data) => frame.current?.contentWindow?.postMessage(data, window.location.origin);

  const onThemeChange = (t) => {
    setDraftTheme(t);
    setDirty(true);
    post({ type: "mu:theme", theme: t });
  };
  const onSiteChange = (s) => {
    setDraftSite(s);
    setDirty(true);
    post({ type: "mu:site", site: s });
  };
  const onFrameLoad = () => {
    post({ type: "mu:theme", theme: draftTheme });
    post({ type: "mu:site", site: draftSite });
  };

  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const t = normalizeTheme(draftTheme);
      const s = stripMeta(draftSite);
      await Promise.all([saveTheme(t), saveSite(s)]);
      previewTheme(t);
      setSiteOverrides(s);
      setDirty(false);
      setMsg({ ok: true, text: "Kaydedildi. Değişiklikler sitede yayında." });
    } catch (err) {
      setMsg({ ok: false, text: `Kaydedilemedi: ${err.code || err.message}. Firestore kurallarını kontrol edin.` });
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setDraftTheme(defaultTheme);
    setDraftSite(defaultSite);
    setDirty(true);
    post({ type: "mu:theme", theme: defaultTheme });
    post({ type: "mu:site", site: defaultSite });
  };

  if (user === undefined) return null;
  if (!user) {
    return (
      <>
        <Seo title="Yönetim" noindex />
        <Login />
      </>
    );
  }

  return (
    <div className="admin">
      <Seo title="Yönetim" noindex />
      <div className="admin__top">
        <div className="container">
          <strong>Yönetim paneli</strong>
          <div className="admin__actions">
            {dirty && <span className="badge badge--accent">Kaydedilmemiş değişiklik</span>}
            <Link to="/" className="btn btn--ghost btn--sm" target="_blank">
              <Icon path={mdiOpenInNew} size={0.7} /> Siteyi aç
            </Link>
            <button className="btn btn--ghost btn--sm" onClick={() => signOut(auth)}>
              <Icon path={mdiLogout} size={0.7} /> Çıkış
            </button>
          </div>
        </div>
      </div>
      <div className="container admin__layout">
        <div className="card admin__panel">
          <div className="tabs" role="tablist">
            <button role="tab" aria-selected={tab === "theme"} onClick={() => setTab("theme")}>Tema</button>
            <button role="tab" aria-selected={tab === "site"} onClick={() => setTab("site")}>Site bilgileri</button>
          </div>
          {tab === "theme" ? (
            <ThemeEditor theme={draftTheme} onChange={onThemeChange} />
          ) : (
            <SiteEditor site={draftSite} onChange={onSiteChange} />
          )}
          {msg && <div className={`alert ${msg.ok ? "alert--success" : "alert--error"}`}>{msg.text}</div>}
          <div className="admin__actions">
            <button className="btn btn--primary" onClick={save} disabled={busy || !dirty}>
              <Icon path={mdiContentSave} size={0.8} /> {busy ? "Kaydediliyor…" : "Kaydet ve yayınla"}
            </button>
            <button className="btn btn--outline" onClick={reset} disabled={busy}>
              <Icon path={mdiRefresh} size={0.8} /> Varsayılana dön
            </button>
          </div>
          <p className="muted" style={{ fontSize: ".8rem", margin: 0 }}>
            Giriş: {user.email}. Tema ve bilgiler Firestore'da <code>settings/theme</code> ve <code>settings/site</code> belgelerinde saklanır.
          </p>
        </div>
        <div className="admin__preview">
          <div className="admin__preview-bar">
            <i /><i /><i />
            <span>Canlı önizleme (kaydetmeden görüntülenir)</span>
          </div>
          <iframe ref={frame} title="Önizleme" src="/?preview=1" onLoad={onFrameLoad} />
        </div>
      </div>
    </div>
  );
};

export default Admin;
